import React from "react";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  SelectChangeEvent,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import InfoSection from "@quest_editor/components/InfoSection";
import { TemplateScopeValue } from "@quest_editor/constants";

type OptionsState = {
  dataCheck: boolean;
  dataCheckPriority: boolean;
  marriage: boolean;
  occupation: boolean;
  industry: boolean;
  numberOfPeople: boolean;
  numberOfChildren: boolean;
  templating: boolean;
  publicationRange: TemplateScopeValue | "";
};

const OptionSection = ({
  options,
  onCheckboxChange,
  onSelectChange,
  templateScopes,
}: {
  options: OptionsState;
  onCheckboxChange: (
    key: keyof OptionsState,
  ) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectChange: (e: SelectChangeEvent<TemplateScopeValue | "">) => void;
  templateScopes: { value: string; label: string }[];
}) => {
  const { t } = useTranslation("questEditor");

  return (
    <InfoSection
      title={t("promotion_edit.option.title")}
      rows={[
        {
          label: t("promotion_edit.option.form.data_check.label"),
          children: (
            <Stack direction="column">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={options.dataCheck}
                    onChange={onCheckboxChange("dataCheck")}
                  />
                }
                label={t("promotion_edit.option.form.data_check.yes")}
                sx={{ color: "text.secondary" }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={options.dataCheckPriority}
                    onChange={onCheckboxChange("dataCheckPriority")}
                  />
                }
                label={t("promotion_edit.option.form.data_check.priority")}
                sx={{ color: "text.secondary", pl: 4 }}
              />
            </Stack>
          ),
        },
        {
          label: t("promotion_edit.option.form.profile.label"),
          children: (
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              flexWrap="wrap"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={options.marriage}
                    onChange={onCheckboxChange("marriage")}
                  />
                }
                label={t("promotion_edit.option.form.profile.marriage")}
                sx={{ color: "text.secondary" }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={options.occupation}
                    onChange={onCheckboxChange("occupation")}
                  />
                }
                label={t("promotion_edit.option.form.profile.occupation")}
                sx={{ color: "text.secondary" }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={options.industry}
                    onChange={onCheckboxChange("industry")}
                  />
                }
                label={t("promotion_edit.option.form.profile.industry")}
                sx={{ color: "text.secondary" }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={options.numberOfPeople}
                    onChange={onCheckboxChange("numberOfPeople")}
                  />
                }
                label={t("promotion_edit.option.form.profile.number_of_people")}
                sx={{ color: "text.secondary" }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={options.numberOfChildren}
                    onChange={onCheckboxChange("numberOfChildren")}
                  />
                }
                label={t("promotion_edit.option.form.profile.number_of_children")}
                sx={{ color: "text.secondary" }}
              />
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {t("promotion_edit.option.form.profile.sub_note")}
              </Typography>
            </Stack>
          ),
        },
        {
          label: t("promotion_edit.option.form.templating.label"),
          children: (
            <Stack
              direction="row"
              alignItems="center"
              spacing={4}
              flexWrap="wrap"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={options.templating}
                    onChange={onCheckboxChange("templating")}
                  />
                }
                label={t("promotion_edit.option.form.templating.yes")}
                sx={{ color: "text.secondary" }}
              />
              <FormControl size="small" sx={{ width: "50%" }}>
                <InputLabel id="publication-range-label">
                  {t(
                    "promotion_edit.option.form.templating.publication_range.label",
                  )}
                </InputLabel>
                <Select
                  labelId="publication-range-label"
                  label={t(
                    "promotion_edit.option.form.templating.publication_range.label",
                  )}
                  value={options.publicationRange}
                  onChange={onSelectChange}
                >
                  {templateScopes.map((ts) => (
                    <MenuItem key={ts.value} value={ts.value}>
                      {ts.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          ),
        },
      ]}
    />
  );
};

export default OptionSection;
